import type { ReactNode } from 'react'
import { Card, Media } from '../../components/ui'
import { BusinessHours } from './BusinessHours'
import { directionsUrl } from './directions'
import type { Branch } from '../../types/storefront'

export interface BranchCardProps {
  branch: Branch
  /** Adds the opening-hours block; the branches page wants it, the home teaser does not. */
  showHours?: boolean
  /** Actions under the card body, e.g. a link into the booking wizard. */
  footer?: ReactNode
}

export function BranchCard({ branch, showHours = false, footer }: BranchCardProps) {
  const maps = directionsUrl(branch)
  const address = [branch.address, branch.city].filter(Boolean).join(', ')

  return (
    <Card padded={false} className="flex h-full flex-col overflow-hidden rounded-lg">
      <div className="aspect-[16/9] w-full overflow-hidden bg-surface-container-high">
        <Media src={branch.image} alt={branch.name} kind="branch" />
      </div>

      <div className="flex flex-1 flex-col gap-3 p-6">
        <h3 className="font-display text-headline-sm text-on-surface">{branch.name}</h3>
        {address && <p className="font-body-md text-body-md text-on-surface-variant">{address}</p>}
        {branch.phone && (
          <a
            href={`tel:${branch.phone}`}
            className="inline-flex items-center gap-2 font-body-md text-body-md text-primary hover:underline"
          >
            <span className="material-symbols-outlined text-[18px]">call</span>
            {branch.phone}
          </a>
        )}

        {showHours && (
          <div className="mt-2 border-t border-primary/10 pt-4">
            <span className="mb-3 block font-label-caps text-label-caps uppercase text-primary">Opening hours</span>
            <BusinessHours hours={branch.business_hours} />
          </div>
        )}

        {maps && (
          <a
            href={maps}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-auto inline-flex items-center gap-2 pt-4 font-label-caps text-label-caps uppercase text-primary hover:underline underline-offset-4"
          >
            Get directions
            <span className="material-symbols-outlined text-[14px]">arrow_outward</span>
          </a>
        )}

        {footer && <div className="mt-4 flex flex-wrap gap-3">{footer}</div>}
      </div>
    </Card>
  )
}
